import { motion } from "framer-motion";
import { Link } from "wouter";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface PricingCardCta {
  label: string;
  path?: string;
  onClick?: () => void;
}

export interface PricingCardMeta {
  label: string;
  value: string;
}

export interface PricingCardProps {
  name: string;
  price: string;
  cadence: string;
  billing?: string;
  tagline: string;
  features: string[];
  cta: PricingCardCta;
  featured?: boolean;
  badgeLabel?: string;
  meta?: PricingCardMeta[];
  notIncluded?: string;
  index?: number;
}

export function PricingCard({
  name,
  price,
  cadence,
  billing,
  tagline,
  features,
  cta,
  featured = false,
  badgeLabel = "Most Popular",
  meta,
  notIncluded,
  index = 0,
}: PricingCardProps) {
  const button = (
    <Button
      onClick={cta.onClick}
      className={`w-full ${
        featured ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""
      }`}
      variant={featured ? "default" : "outline"}
    >
      {cta.label}
    </Button>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`pricing-card-${name.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex flex-col rounded-3xl p-8 ${
        featured
          ? "border-2 border-indigo-500 shadow-xl relative"
          : "border border-border shadow-sm"
      } bg-card text-card-foreground`}
    >
      {featured && (
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-indigo-500 text-white text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full">
          {badgeLabel}
        </div>
      )}
      <h3 className="text-xl font-bold mb-2">{name}</h3>
      <div className="mb-1">
        <span className="text-4xl font-extrabold">{price}</span>
        <span className="text-sm text-muted-foreground ml-1">{cadence}</span>
      </div>
      {billing && (
        <div className="text-xs text-muted-foreground mb-3">{billing}</div>
      )}
      {!billing && <div className="mb-3" />}
      <p className="text-sm text-muted-foreground mb-6 min-h-[40px]">
        {tagline}
      </p>
      {meta && meta.length > 0 && (
        <dl className="mb-6 space-y-1.5 rounded-xl bg-muted/50 p-3 text-xs">
          {meta.map((item, i) => (
            <div key={i} className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">{item.label}</dt>
              <dd className="font-semibold text-foreground">{item.value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="mb-8 mt-auto">
        {cta.path ? <Link href={cta.path}>{button}</Link> : button}
      </div>
      <div className="text-sm font-semibold mb-4">What&apos;s included</div>
      <ul className="space-y-3">
        {features.map((feature, j) => (
          <li key={j} className="flex items-start gap-3 text-sm">
            <CheckCircle2 className="h-5 w-5 text-indigo-500 shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      {notIncluded && (
        <p className="mt-4 text-xs text-muted-foreground border-t border-border pt-4">
          {notIncluded}
        </p>
      )}
    </motion.div>
  );
}
